import { TaxApiError } from "./errors";
import { answerLost } from "./failure";
import { isIdempotentReplayAllowed } from "./replay-safety";

/**
 * Uploads, imports and task creation commit server-side even when the API
 * Gateway gives up first (504 `response_timeout`). Every write carries an
 * Idempotency-Key, so asking again with the same key either returns the stored
 * result or reports that the first attempt is still running. That re-ask is the
 * "verifying" step: the person sees "may have completed — verifying…" and then
 * a normal success, not an error for work that actually succeeded.
 */
export const COMMIT_VERIFY_DELAYS_MS: readonly number[] = [2_000, 5_000, 10_000];

/**
 * Replay only routes verified to return the stored result for a repeated key
 * (see `replay-safety.ts`). Any other write is never re-sent on the person's
 * behalf: the screen says it may have completed and asks them to check first.
 */
export function shouldVerifyCommit(error: unknown): boolean {
  if (!(error instanceof TaxApiError) || !error.method || !error.path) return false;
  if (!isIdempotentReplayAllowed(error.method, error.path)) return false;
  // A dropped connection (status 0, online) may have reached the server too. On these routes a
  // repeated key replays the stored result, so asking again is safe whatever happened.
  const dropped = error.code === "NETWORK" && !error.offline;
  return answerLost(error) || dropped || error.code === "IDEMPOTENCY_IN_PROGRESS";
}

export type CommitVerificationOptions = {
  delaysMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
  signal?: AbortSignal;
};

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Runs `attempt`; if it fails with an unknown outcome, replays it (same
 * idempotency key, so never a duplicate) with a short backoff. The last error
 * is thrown when every replay fails, still flagged as possibly committed.
 */
export async function withCommitVerification<T>(
  attempt: () => Promise<T>,
  options: CommitVerificationOptions = {},
): Promise<T> {
  const delays = options.delaysMs ?? COMMIT_VERIFY_DELAYS_MS;
  const sleep = options.sleep ?? defaultSleep;
  let verifying = false;
  try {
    for (let index = 0; ; index += 1) {
      try {
        return await attempt();
      } catch (error) {
        const delay = delays[index];
        if (delay === undefined || !shouldVerifyCommit(error) || options.signal?.aborted) {
          throw error;
        }
        if (!verifying) {
          verifying = true;
          verificationStore.begin();
        }
        await sleep(delay);
      }
    }
  } finally {
    if (verifying) verificationStore.end();
  }
}

/** How many writes are currently being re-checked; drives the global "verifying" banner. */
class VerificationStore {
  #count = 0;
  readonly #listeners = new Set<() => void>();

  begin() {
    this.#count += 1;
    this.#emit();
  }

  end() {
    this.#count = Math.max(0, this.#count - 1);
    this.#emit();
  }

  readonly subscribe = (listener: () => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  readonly getSnapshot = () => this.#count;

  #emit() {
    for (const listener of this.#listeners) listener();
  }
}

export const verificationStore = new VerificationStore();
